// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

/**
 * A React hook that observes the size of an element and provides the element's
 * width and height.
 *
 * @param ref - A React ref object that points to the element to observe.
 * @param options - Configuration options for the resize observer
 */
export function useResizeObserver<T extends Element>(
  ref: RefObject<T>,
  options: {
    /** Debounce delay in milliseconds to avoid rapid updates during animations */
    debounceMs?: number;
    /** Minimum delay before first measurement to allow for initial layout */
    initialDelayMs?: number;
  } = {}
) {
  const { debounceMs = 50, initialDelayMs = 100 } = options;

  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const initialTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const hasInitialMeasurement = useRef(false);

  // Seed the size synchronously before the first paint so consumers never
  // render against a zero width. Relying only on the ResizeObserver leaves a
  // window where width is 0 (worst on Suspense remounts), which collapses
  // timeline scales and intervals. Marking the initial measurement done here
  // lets the observer's first async notification take the debounced path.
  useLayoutEffect(() => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setWidth(rect.width);
      setHeight(rect.height);
      hasInitialMeasurement.current = true;
    }
  }, [ref]);

  const updateSize = useCallback(
    (newWidth: number, newHeight: number) => {
      // Clear any pending debounced update
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      // Debounce the size update to avoid rapid changes during animations
      debounceTimeoutRef.current = setTimeout(() => {
        setWidth(newWidth);
        setHeight(newHeight);
      }, debounceMs);
    },
    [debounceMs]
  );

  useEffect(() => {
    let lastRef = ref.current;
    let observer: ResizeObserver | null = null;

    // Function to setup the observer
    const setupObserver = () => {
      // Clean up previous observer if it exists
      if (observer) {
        observer.disconnect();
      }

      // If we have a ref, create and setup new observer
      if (ref.current) {
        observer = new ResizeObserver((entries) => {
          entries.forEach((entry) => {
            const newWidth = entry.contentRect.width;
            const newHeight = entry.contentRect.height;

            // For the initial measurement, add a delay to allow layout to stabilize
            if (!hasInitialMeasurement.current) {
              if (initialTimeoutRef.current) {
                clearTimeout(initialTimeoutRef.current);
              }

              initialTimeoutRef.current = setTimeout(() => {
                hasInitialMeasurement.current = true;
                updateSize(newWidth, newHeight);
              }, initialDelayMs);
            } else {
              // For subsequent measurements, use normal debounced update
              updateSize(newWidth, newHeight);
            }
          });
        });

        observer.observe(ref.current);
        lastRef = ref.current;
      }
    };

    // Initial setup
    setupObserver();

    // Check for ref changes periodically with better detection
    const intervalId = setInterval(() => {
      if (ref.current !== lastRef) {
        hasInitialMeasurement.current = false; // Reset for new element
        setupObserver();
      }
    }, 100);

    return () => {
      clearInterval(intervalId);
      observer?.disconnect();
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      if (initialTimeoutRef.current) {
        clearTimeout(initialTimeoutRef.current);
      }
    };
  }, [ref, updateSize, initialDelayMs]);

  // Method to force immediate remeasurement (useful after animations)
  const remeasure = useCallback(() => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setWidth(rect.width);
      setHeight(rect.height);
    }
  }, [ref]);

  return { width, height, remeasure };
}
